import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ErrorComponent from '@/components/error';
import { useEntityContext } from '@/entity/hooks/use-entity-context';
import Section from '../section';
import CurrentData from './components/current-data';
import Loading from './components/loading';
import OnboardingData from './components/onboarding-data';
import OnboardingTabs from './components/onboarding-tabs';
import { CURRENT_ONBOARDING_ID, CURRENT_TAB_ID, ONBOARDING_PANEL_ID, ONBOARDINGS_ID } from './constants';
import useGetOnboardings from './hooks/use-get-onboardings';
import useIsStuck from './hooks/use-is-stuck';
import useKeepSectionInView from './hooks/use-keep-section-in-view';
import useShowOnboarding, { scrollToOnboarding } from './hooks/use-show-onboarding';
import { getOnboardingByIdOrFallback } from './utils';
const Onboardings = () => {
  const $ = _c(70);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "onboardings"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t0);
  const {
    kind
  } = useEntityContext();
  const router = useRouter();
  const {
    isPending,
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError
  } = useGetOnboardings();
  const {
    onboardingId,
    setOnboardingId
  } = useShowOnboarding();
  const {
    sentinelRef,
    isStuck
  } = useIsStuck();
  let isCurrentSelected;
  let onboardings;
  let selectedOnboarding;
  let selectedTabId;
  let t1;
  if ($[1] !== data?.pages || $[2] !== onboardingId) {
    onboardings = data?.pages.flatMap(_temp) ?? [];
    selectedOnboarding = getOnboardingByIdOrFallback(onboardings, onboardingId);
    isCurrentSelected = onboardingId === CURRENT_ONBOARDING_ID;
    selectedTabId = isCurrentSelected ? CURRENT_ONBOARDING_ID : selectedOnboarding?.id;
    t1 = isCurrentSelected || onboardings.some(onboarding => onboarding.id === onboardingId);
    $[1] = data?.pages;
    $[2] = onboardingId;
    $[3] = isCurrentSelected;
    $[4] = onboardings;
    $[5] = selectedOnboarding;
    $[6] = selectedTabId;
    $[7] = t1;
  } else {
    isCurrentSelected = $[3];
    onboardings = $[4];
    selectedOnboarding = $[5];
    selectedTabId = $[6];
    t1 = $[7];
  }
  const isTargetLoaded = t1;
  const isPagingToTarget = Boolean(onboardingId) && !isTargetLoaded && hasNextPage && !isFetchNextPageError;
  const hasHandledHashRef = useRef(false);
  const {
    stripRef,
    panelRef,
    rememberSectionInView
  } = useKeepSectionInView(selectedTabId);
  let t2;
  if ($[8] !== rememberSectionInView || $[9] !== selectedTabId || $[10] !== setOnboardingId) {
    t2 = id => {
      if (id === selectedTabId) {
        return;
      }
      rememberSectionInView();
      setOnboardingId(id);
    };
    $[8] = rememberSectionInView;
    $[9] = selectedTabId;
    $[10] = setOnboardingId;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const handleSelect = t2;
  let t3;
  if ($[12] !== fetchNextPage || $[13] !== isCurrentSelected || $[14] !== isFetchingNextPage || $[15] !== isPagingToTarget || $[16] !== onboardingId || $[17] !== selectedOnboarding || $[18] !== setOnboardingId) {
    t3 = () => {
      if (!selectedOnboarding || isFetchingNextPage || isCurrentSelected) {
        return;
      }
      if (isPagingToTarget) {
        fetchNextPage();
      } else {
        if (onboardingId !== selectedOnboarding.id) {
          setOnboardingId(selectedOnboarding.id);
        }
      }
    };
    $[12] = fetchNextPage;
    $[13] = isCurrentSelected;
    $[14] = isFetchingNextPage;
    $[15] = isPagingToTarget;
    $[16] = onboardingId;
    $[17] = selectedOnboarding;
    $[18] = setOnboardingId;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  const t4 = selectedOnboarding?.id;
  let t5;
  if ($[20] !== isCurrentSelected || $[21] !== isFetchingNextPage || $[22] !== isPagingToTarget || $[23] !== onboardingId || $[24] !== t4) {
    t5 = [t4, onboardingId, isPagingToTarget, isFetchingNextPage, isCurrentSelected];
    $[20] = isCurrentSelected;
    $[21] = isFetchingNextPage;
    $[22] = isPagingToTarget;
    $[23] = onboardingId;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  useEffect(t3, t5);
  let t6;
  if ($[26] !== isTargetLoaded || $[27] !== router) {
    t6 = () => {
      if (hasHandledHashRef.current || !isTargetLoaded) {
        return;
      }
      hasHandledHashRef.current = true;
      if (router.asPath.split("#")[1] === ONBOARDINGS_ID) {
        scrollToOnboarding();
      }
    };
    $[26] = isTargetLoaded;
    $[27] = router;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== isTargetLoaded) {
    t7 = [isTargetLoaded];
    $[29] = isTargetLoaded;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  useEffect(t6, t7);
  let t8;
  if ($[31] !== t) {
    t8 = t(_temp2);
    $[31] = t;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== hasNextPage || $[34] !== onboardings.length) {
    t9 = onboardings.length ? <span className="text-body-2 text-tertiary">({hasNextPage ? `${onboardings.length}+` : onboardings.length})</span> : null;
    $[33] = hasNextPage;
    $[34] = onboardings.length;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== t) {
    t10 = t(_temp3);
    $[36] = t;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== isPending) {
    t11 = isPending && <Loading />;
    $[38] = isPending;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== error) {
    t12 = error && <ErrorComponent error={error} />;
    $[40] = error;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  let t13;
  if ($[42] !== error || $[43] !== isPending || $[44] !== kind || $[45] !== selectedOnboarding || $[46] !== t) {
    t13 = !isPending && !error && !selectedOnboarding && <div className="flex flex-col gap-4"><p className="text-body-2 text-primary">{t(_temp4, {
          kind
        })}</p><CurrentData /></div>;
    $[42] = error;
    $[43] = isPending;
    $[44] = kind;
    $[45] = selectedOnboarding;
    $[46] = t;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== fetchNextPage || $[49] !== handleSelect || $[50] !== hasNextPage || $[51] !== isCurrentSelected || $[52] !== isFetchingNextPage || $[53] !== isPagingToTarget || $[54] !== isStuck || $[55] !== onboardings || $[56] !== panelRef || $[57] !== selectedOnboarding || $[58] !== selectedTabId || $[59] !== sentinelRef || $[60] !== stripRef) {
    t14 = selectedOnboarding && <div className="flex flex-col"><div ref={sentinelRef} className="h-px" /><div ref={stripRef} className={cx("z-popover -mx-1 px-1 pt-2 pb-2 bg-primary border-b border-transparent md:sticky", {
        "md:top-0": !isStuck,
        "md:-top-13 md:-mt-7 md:pt-9 md:border-tertiary": isStuck
      })}><OnboardingTabs onboardings={onboardings} selectedId={selectedTabId ?? selectedOnboarding.id} onSelect={handleSelect} onLoadMore={fetchNextPage} hasMore={hasNextPage} isLoadingMore={isFetchingNextPage} /></div>{isPagingToTarget ? <div className="mt-3"><Loading /></div> : <div ref={panelRef} role="tabpanel" id={ONBOARDING_PANEL_ID} aria-labelledby={isCurrentSelected ? CURRENT_TAB_ID : selectedOnboarding.id} className="mt-3 md:[&_*]:scroll-mt-32">{isCurrentSelected ? <CurrentData /> : <OnboardingData key={selectedOnboarding.id} onboarding={selectedOnboarding} />}</div>}</div>;
    $[48] = fetchNextPage;
    $[49] = handleSelect;
    $[50] = hasNextPage;
    $[51] = isCurrentSelected;
    $[52] = isFetchingNextPage;
    $[53] = isPagingToTarget;
    $[54] = isStuck;
    $[55] = onboardings;
    $[56] = panelRef;
    $[57] = selectedOnboarding;
    $[58] = selectedTabId;
    $[59] = sentinelRef;
    $[60] = stripRef;
    $[61] = t14;
  } else {
    t14 = $[61];
  }
  let t15;
  if ($[62] !== t10 || $[63] !== t11 || $[64] !== t12 || $[65] !== t13 || $[66] !== t14 || $[67] !== t8 || $[68] !== t9) {
    t15 = <Section title={t8} titleSuffix={t9} id={ONBOARDINGS_ID} aria-label={t10}>{t11}{t12}{t13}{t14}</Section>;
    $[62] = t10;
    $[63] = t11;
    $[64] = t12;
    $[65] = t13;
    $[66] = t14;
    $[67] = t8;
    $[68] = t9;
    $[69] = t15;
  } else {
    t15 = $[69];
  }
  return t15;
};
export default Onboardings;
function _temp(page) {
  return page?.data ?? [];
}
function _temp2($) {
  return $.title;
}
function _temp3($_0) {
  return $_0.title;
}
function _temp4($_1) {
  return $_1.noOnboardings;
}